import React, { useCallback, useEffect } from 'react';
import { ReusableModal, ReusableModalProps } from '../modals/ReusableModal';
import { Interconnect, PathComponent, Point, PriceComponent } from '@/types';
import { useSelector } from 'react-redux';
import { selectFilteredPathComponents } from '../filters/advancedFiltersSelectors';
import { useClientPagination } from '../helpers/hooks';
import { PointListItem } from '../points/PointListItem';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { InterconnectListItem } from '../interconnects/InterconnectListItem';
import {
  PathComponentFilters,
  PathComponentFiltersRef,
} from './PathComponentFilters';
import { colors } from '../utils/colors';
import { AdvancedFiltersState } from '../filters/advancedFiltersSlice';
import { useGetPriceComponentsQuery } from '../services/edgeApi';
import { PoolListItem } from '../priceComponents/PoolListItem';
import toast from 'react-hot-toast';

export type PathSelectorModalProps = {
  isOpen: ReusableModalProps['isOpen'];
  size?: ReusableModalProps['size'];
  lockedTsps?: AdvancedFiltersState['pathComponents']['tsps'];
  startingLocation?: string;
  onClose: ReusableModalProps['onClose'];
  onSelect: (selected: PathComponent) => void;
  onFinish?: () => void;
};

export const PathSelectorModal: React.FC<PathSelectorModalProps> = ({
  isOpen,
  size = 'lg',
  lockedTsps,
  onClose,
  onSelect,
  onFinish,
  startingLocation,
}) => {
  useGetPriceComponentsQuery({});
  const filtersRef = React.useRef<PathComponentFiltersRef>(null);
  const pathComponents = useSelector(selectFilteredPathComponents);
  const { slice: paginatedPathComponents, getNext } = useClientPagination(
    pathComponents,
    20,
  );

  const onSearch = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'F3' || (e.ctrlKey && e.key === 'f')) {
        e.preventDefault();
        filtersRef.current?.focusSearchBox();
      }
    },
    [filtersRef],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', onSearch);
      return () => {
        window.removeEventListener('keydown', onSearch);
      };
    }
    return;
  }, [isOpen, onSearch]);

  const _onSelect = useCallback(
    (component: PathComponent) => {
      if (component.disabled) {
        toast.error(
          `${component.name} is disabled and cannot be added to the path`,
        );
        return;
      }
      onSelect(component);
      toast.success(`${component.name} added`);
      filtersRef.current?.clearSearchBox();
    },
    [onSelect, filtersRef.current],
  );

  return (
    <ReusableModal isOpen={isOpen} onClose={onClose} size={size}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'var(--bs-body-bg)',
          zIndex: 1,
        }}
        className="pt-3"
      >
        {startingLocation && (
          <h4 className="text-center">On {startingLocation}</h4>
        )}
        <PathComponentFilters ref={filtersRef} lockedTsps={lockedTsps} />
      </div>
      <div className="mt-3">
        {paginatedPathComponents.map((component) => {
          if (component.type === 'point') {
            return (
              <div key={`point-${component.id}`}>
                <p className="fw-bold" style={{ color: colors.matteBlue }}>
                  Point
                </p>
                <PointListItem
                  point={component.instance as Point}
                  onClick={() => _onSelect(component)}
                />
              </div>
            );
          }
          if (component.type === 'interconnect') {
            return (
              <div key={`interconnect-${component.id}`}>
                <p
                  className="fw-bold"
                  style={{ color: colors.elements.interconnectColor }}
                >
                  Interconnect
                </p>
                <InterconnectListItem
                  disabled={component.disabled}
                  interconnect={component.instance as Interconnect}
                  startPoint={component.points[0]}
                  endPoint={component.points[1]}
                  displacement={component.attributes.displacement}
                  onClick={() => _onSelect(component)}
                />
              </div>
            );
          }
          if (component.type === 'pool') {
            const pool = component.instance as PriceComponent;
            return (
              <div key={`pool-${component.id}`}>
                <p
                  className="fw-bold"
                  style={{ color: colors.elements.deliveryNode }}
                >
                  Pool
                </p>
                <PoolListItem
                  pool={pool}
                  point={component.attributes.point as Point}
                  multiPoint={pool.points.length > 1}
                  onClick={() => _onSelect(component)}
                />
              </div>
            );
          }
          return null;
        })}
        <InfiniteScrollObserver onScrollIn={getNext} />
      </div>
      {onFinish && (
        <div
          className="py-3"
          style={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'var(--bs-body-bg)',
          }}
        >
          <button className="btn btn-primary" onClick={onFinish}>
            Done
          </button>
        </div>
      )}
    </ReusableModal>
  );
};
